import React, { ChangeEvent, useMemo } from 'react';
import { Alert, Box, Icon, Input, Text } from '@nimbus-ds/components';
import { MoneyIcon } from '@nimbus-ds/icons';
import { useStoreInfo } from 'commons/hooks';
import { MoneyObjectInterface } from 'commons/types';
import { formatCurrency, useTranslationWithPrefix } from 'commons/utils';
import { getMinOrderAmountMx, hasMinOrderAmountErrorMx } from '../../utils';

interface MinOrderAmountInputMxProps {
  value: MoneyObjectInterface;
  onChange: (id: number, minOrderAmount: MoneyObjectInterface) => void;
  installmentId: number;
  installmentQuantity: number;
  disabled?: boolean;
}

function MinOrderAmountInputMx(props: MinOrderAmountInputMxProps) {
  const { t } = useTranslationWithPrefix('installments.minOrderAmountInput');
  const { storeInfo } = useStoreInfo();

  const minimumOrderAmount = useMemo(
    () => getMinOrderAmountMx(props.installmentQuantity),
    [props.installmentQuantity],
  );

  const hasValidationError = useMemo(
    () =>
      hasMinOrderAmountErrorMx(props.value.value, props.installmentQuantity),
    [props.value.value, props.installmentQuantity],
  );

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = Number.parseFloat(e.target.value);
    if (!e.target.value || Number.isNaN(newValue) || newValue <= 0) {
      props.onChange(props.installmentId, {
        currency: props.value.currency,
        value: 0,
      });
    } else {
      props.onChange(props.installmentId, {
        currency: props.value.currency,
        value: newValue,
      });
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="2">
      <Text color="neutral-textHigh">{t('title')}</Text>
      <Box display="flex" flexDirection="column" gap="2" width="15rem">
        <Input
          placeholder={t('noAmount')}
          append={<Icon source={<MoneyIcon />} />}
          value={props.value.value || ''}
          onChange={onInputChange}
          disabled={props.disabled}
          type="number"
          onKeyDown={(evt) =>
            ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
          }
        />
      </Box>
      {hasValidationError && (
        <Alert appearance="danger">
          {t('errorMinimumAmount', {
            minimumAmount: formatCurrency(
              {
                value: minimumOrderAmount,
                currency: props.value.currency,
              },
              storeInfo,
            ),
            installmentQuantity: props.installmentQuantity,
          })}
        </Alert>
      )}
      {props.value?.value && !hasValidationError ? (
        <Text color="neutral-textHigh">
          {t('amountByInstallment', {
            value: formatCurrency(
              {
                value: props.value.value / props.installmentQuantity,
                currency: props.value.currency,
              },
              storeInfo,
            ),
            installmentQuantity: props.installmentQuantity,
          })}
        </Text>
      ) : null}
    </Box>
  );
}

export default MinOrderAmountInputMx;
